/** @jsx jsx */
import React from 'react';
import styled from '@emotion/styled';
import { ChevronDown } from '@commandbar/design-system/icons/react';
import StyledIconContainer from '../shared/StyledIconContainer';
import type { IThemeV2Type } from '@cb/types/entities/theme';
import useComponentOverrides from '../../helpers/useComponentOverrides';
import { CSSObject, css, jsx } from '@emotion/core';

const styles: CSSObject = {
  appearance: 'none',
  display: 'flex',
  height: 'var(--form-control-height)',
  padding: '0px var(--form-control-padding-x)',
  justifyContent: 'space-between',
  alignItems: 'center',
  flex: '1 0 0',
  alignSelf: 'stretch',
  cursor: 'pointer',
  userSelect: 'none',
  borderRadius: 'var(--layout-radius-button)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--form-control-border-color)',
  background: 'var(--form-control-background)',
  boxShadow: 'var(--form-control-shadow) var(--form-control-shadow-color)',

  color: 'var(--content-primary)',

  /* label/base/medium */
  fontFamily: 'var(--font-font-family)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-medium)' as unknown as number,
  lineHeight: 'var(--font-line-height-normal)',

  '&:hover': {
    background: 'var(--form-control-background-hover)',
    borderColor: 'var(--form-control-border-color-hover)',
    boxShadow: 'var(--form-control-shadow) var(--form-control-shadow-color)',
  },

  '&:focus': {
    borderColor: 'var(--base-accent)',
    boxShadow: '0px 0px 0px var(--form-control-focus-ring-width) var(--form-control-focus-ring-color)',
    background: 'var(--form-control-background-hover)',
  },

  '&:focus-visible': {
    outline: 'none',
  },
};

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const CaretContainer = styled(StyledIconContainer)`
  position: absolute;
  right: var(--form-control-padding-x);
  color: var(--content-secondary);
`;

const slug = 'select';

type Props = {
  themeV2: { themeV2: IThemeV2Type; mode: 'light_mode' | 'dark_mode' };
};

const SelectWithReset = styled.select`
  all: unset;
  box-sizing: border-box;
`;

export const OptionWithReset = styled.option`
  all: unset;
  box-sizing: border-box;
`;

const StyledSelect = Object.assign(
  React.forwardRef<HTMLSelectElement, Props & React.SelectHTMLAttributes<HTMLSelectElement>>(
    ({ themeV2, children, ...props }, ref) => {
      const { overrides } = useComponentOverrides(slug, themeV2);

      return (
        <Container css={overrides}>
          <SelectWithReset css={[overrides, css(styles)]} ref={ref} {...props}>
            {children}
          </SelectWithReset>
          <CaretContainer>
            <ChevronDown />
          </CaretContainer>
        </Container>
      );
    },
  ),
  {
    slug: slug,
    styles: styles,
    renderMock: (themeV2: IThemeV2Type, mode: 'light_mode' | 'dark_mode') => (
      <StyledSelectMock themeV2={themeV2} mode={mode} />
    ),
  },
);

const StyledSelectMock = ({ themeV2, mode }: { themeV2: IThemeV2Type; mode: 'light_mode' | 'dark_mode' }) => {
  const { overrides } = useComponentOverrides(slug, { themeV2: themeV2, mode: mode });
  return (
    <Container css={overrides}>
      <SelectWithReset css={[overrides, css(styles)]}>
        <OptionWithReset key={'select'}>Select option...</OptionWithReset>
      </SelectWithReset>
      <CaretContainer>
        <ChevronDown />
      </CaretContainer>
    </Container>
  );
};

export default StyledSelect;
